import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';

import { BASE_IMAGE_URL, TRADE_SALES_EMAIL } from '../../tradeBenefitsConstants.ts';
import { useTradeBenefitsContact } from '../../useTradeBenefitsContact.ts';
import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import { useServerVars } from '../../../shared/ServerVarsContext.tsx';

import { type TradeBenefitsPlanningProject_viewer$key } from './__generated__/TradeBenefitsPlanningProject_viewer.graphql.ts';
import { type TradeBenefitsPlanningProject_tradeFirm$key } from './__generated__/TradeBenefitsPlanningProject_tradeFirm.graphql.ts';

import sharedStyles from '../../TradeBenefitsSharedStyles.scss';
import styles from './TradeBenefitsPlanningProject.scss';
import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment TradeBenefitsPlanningProject_viewer on Viewer {
        ...useTradeBenefitsContact_viewer
    }
`;

const tradeFirmFragment = graphql`
    fragment TradeBenefitsPlanningProject_tradeFirm on TradeFirmType {
        ...useTradeBenefitsContact_tradeFirm
    }
`;

const TradeBenefitsPlanningProject: FC<{
    viewer: TradeBenefitsPlanningProject_viewer$key;
    tradeFirm: TradeBenefitsPlanningProject_tradeFirm$key | null | undefined;
    isFirmless?: boolean;
}> = ({ viewer: viewerRef, tradeFirm: tradeFirmRef, isFirmless = false }) => {
    const { isMobile } = useServerVars();
    const viewer = useFragment(viewerFragment, viewerRef);
    const tradeFirm = useFragment(tradeFirmFragment, tradeFirmRef);
    const { isSilverPlus, isAssigned, firstName, repEmail } = useTradeBenefitsContact({
        viewer,
        tradeFirm,
    });

    if (!isSilverPlus && !isFirmless) {
        return null;
    }

    const href = isAssigned ? `mailto:${repEmail}` : `mailto:${TRADE_SALES_EMAIL}`;
    const buttonLabel = isAssigned ? (
        <FormattedMessage
            id="abt.TradeBenefitsPlanningProject.button.assigned"
            defaultMessage="Contact {firstName}"
            values={{ firstName }}
        />
    ) : (
        <FormattedMessage
            id="abt.TradeBenefitsPlanningProject.button.unassigned"
            defaultMessage="Contact Trade Sales"
        />
    );

    return (
        <div
            className={classnames(
                sharedStyles.fullWidthContainer,
                styles.container,
                dibsCss.relative,
                dibsCss.overflowHidden
            )}
        >
            <img
                src={`${BASE_IMAGE_URL}${isMobile ? '/authenticated_contact_module_mobile.jpg' : '/authenticated_contact_module.jpg'}`}
                alt=""
                aria-hidden="true"
                className={classnames(
                    dibsCss.absolute,
                    dibsCss.inset0,
                    dibsCss.hFull,
                    dibsCss.wFull,
                    dibsCss.objectCover
                )}
            />
            <div
                className={classnames(
                    styles.content,
                    dibsCss.relative,
                    dibsCss.maxW1440px,
                    dibsCss.mxAuto,
                    dibsCss.hFull,
                    dibsCss.pSmall,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyCenter,
                    dibsCss.flexCol,
                    dibsCss.gapMedium
                )}
            >
                <HeadingLevel>
                    {Heading => (
                        <Heading
                            className={classnames(
                                styles.heading,
                                sharedStyles.headingLarge,
                                dibsCss.textHeaderXLargeNormal,
                                dibsCss.m0,
                                dibsCss.textWhite
                            )}
                        >
                            <FormattedMessage
                                id="abt.TradeBenefitsPlanningProject.title"
                                defaultMessage="In the planning stages{break} of a <italic>new project?</italic>"
                                values={{
                                    italic: content => (
                                        <span className={sharedStyles.headingLargeItalic}>
                                            {content}
                                        </span>
                                    ),
                                    break: <br className={styles.mobileBreak} />,
                                }}
                            />
                        </Heading>
                    )}
                </HeadingLevel>
                {!isMobile && (
                    <p className={classnames(dibsCss.textBodyHeaderLargeNormal, dibsCss.textWhite)}>
                        <FormattedMessage
                            id="abt.TradeBenefitsPlanningProject.description"
                            defaultMessage="Let us help you fulfill your creative vision."
                        />
                    </p>
                )}
                <Button
                    className={styles.button}
                    href={href}
                    target="_blank"
                    style="circle"
                    onClick={() =>
                        trackTradeBenefitsEvent({
                            stepInteractionName: 'contact clicked',
                            trigger: 'planning a project',
                        })
                    }
                    customColors={{
                        background: 'sassyColorWhite',
                        text: 'sassyColorNoir',
                        hoverText: 'sassyColorNoir',
                        hoverBackground: 'sassyColorAlabaster',
                    }}
                >
                    {buttonLabel}
                </Button>
            </div>
        </div>
    );
};

export { TradeBenefitsPlanningProject };
